import { useState, useRef, useCallback } from "react";
import {
  Box, Typography, Select, MenuItem, Divider,
  Button, TextField, IconButton, Tooltip, Switch,
  FormControlLabel, Collapse, Chip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import ToolDialog from "../ToolDialog";

const LAYER_URL = "https://app.mapgeoid.com/api-service/layers";

// ─── constants ────────────────────────────────────────────────────────────────

const GEOM_TYPES = ["Point", "LineString", "Polygon"];

const DTYPES = [
  { value: "string", label: "String", badge: "str", bg: "#E6F1FB", color: "#185FA5" },
  { value: "integer", label: "Integer", badge: "int", bg: "#FAEEDA", color: "#633806" },
  { value: "float", label: "Float", badge: "float", bg: "#FAEEDA", color: "#854F0B" },
  { value: "bool", label: "Boolean", badge: "bool", bg: "#E1F5EE", color: "#085041" },
  { value: "photo", label: "Photo (str[])", badge: "str[]", bg: "#EEEDFE", color: "#3C3489" },
];

const AUTO_VALUES = [
  { value: "geom.centroid.x", label: "geom.centroid.x" },
  { value: "geom.centroid.y", label: "geom.centroid.y" },
  { value: "geom.length", label: "geom.length" },
  { value: "geom.area", label: "geom.area" },
  { value: "geom.perimeter", label: "geom.perimeter" },
];

const DTYPE_MAP = Object.fromEntries(DTYPES.map((d) => [d.value, d]));

const GEOM_STYLE = {
  Point: { bg: "#E1F5EE", border: "#1D9E75" },
  LineString: { bg: "#E6F1FB", border: "#185FA5" },
  Polygon: { bg: "#EEEDFE", border: "#534AB7" },
};

// ─── default templates ────────────────────────────────────────────────────────

const TEMPLATES = [
  {
    layer_name: "BLANK",
    geometry_type: "Point",
    schema: [],
  },
  {
    layer_name: "LANDMARK",
    geometry_type: "Point",
    schema: [
      { key: "name", label: "Landmark Name", data_type: "string", multi: false, placeholder: "Enter landmark name" },
      { key: "latitude", label: "Latitude", data_type: "float", multi: false, auto: true, default: "geom.centroid.y", placeholder: "Latitude (e.g., 22.5726)" },
      { key: "longitude", label: "Longitude", data_type: "float", multi: false, auto: true, default: "geom.centroid.x", placeholder: "Longitude (e.g., 88.3639)" },
      {
        key: "category", label: "Category", data_type: "string", multi: false, placeholder: "Select category", options: [
          { id: 1, value: "School" }, { id: 2, value: "College" }, { id: 3, value: "Temple" }, { id: 4, value: "Masjid" },
          { id: 5, value: "Church" }, { id: 6, value: "Bus Stand" }, { id: 7, value: "Government Office" },
          { id: 8, value: "Post Office" }, { id: 9, value: "Petrol Pump" }, { id: 10, value: "Hotel" },
          { id: 11, value: "Park" }, { id: 12, value: "Pharmacy" }, { id: 13, value: "Hospital" }, { id: 14, value: "Others" },
        ]
      },
      { key: "block_name", label: "Block Name", data_type: "string", multi: false, placeholder: "Enter block name" },
      { key: "district_name", label: "District Name", data_type: "string", multi: false, placeholder: "Enter district name" },
      { key: "state_name", label: "State Name", data_type: "string", multi: false, placeholder: "Enter state name" },
      { key: "multiple_images", label: "Photos", data_type: "photo", multi: true, placeholder: "Upload photos" },
    ],
  },
  {
    layer_name: "CROSSING",
    geometry_type: "LineString",
    schema: [
      { key: "name", label: "Crossing Name", data_type: "string", multi: false, placeholder: "Enter crossing name" },
      {
        key: "type", label: "Crossing Type", data_type: "string", multi: false, placeholder: "Select type", options: [
          { id: 1, value: "Bridge" }, { id: 2, value: "Culvert" }, { id: 3, value: "Road Cross" }, { id: 4, value: "NH Cross" }, { id: 5, value: "Rail Cross" },
        ]
      },
      { key: "length_metre", label: "Length (m)", data_type: "float", multi: false, placeholder: "Length in meters (e.g., 10.23)" },
      { key: "block_name", label: "Block Name", data_type: "string", multi: false, placeholder: "Enter block name" },
      { key: "district_name", label: "District Name", data_type: "string", multi: false, placeholder: "Enter district name" },
      { key: "state_name", label: "State Name", data_type: "string", multi: false, placeholder: "Enter state name" },
      { key: "multiple_images", label: "Photos", data_type: "photo", multi: true, placeholder: "Upload photos" },
      { key: "between_pole_no", label: "Between Pole Numbers", data_type: "string", multi: false, placeholder: "e.g., 55/6-55/7" },
      { key: "between_station", label: "Between Stations", data_type: "string", multi: false, placeholder: "e.g., Station_A to Station_B" },
      { key: "zone", label: "Zone", data_type: "string", multi: false, placeholder: "Enter zone name" },
    ],
  },
  {
    layer_name: "RI",
    geometry_type: "Point",
    schema: [
      { key: "name", label: "RI Name", data_type: "string", multi: false, placeholder: "Enter RI point name" },
      { key: "latitude", label: "Latitude", data_type: "float", multi: false, auto: true, default: "geom.centroid.y", placeholder: "Latitude (e.g., 22.5726)" },
      { key: "longitude", label: "Longitude", data_type: "float", multi: false, auto: true, default: "geom.centroid.x", placeholder: "Longitude (e.g., 88.3639)" },
      { key: "type", label: "RI Type", data_type: "string", multi: false, placeholder: "RI", options: [{ id: 1, value: "RI" }] },
      { key: "block_name", label: "Block Name", data_type: "string", multi: false, placeholder: "Enter block name" },
      { key: "district_name", label: "District Name", data_type: "string", multi: false, placeholder: "Enter district name" },
      { key: "state_name", label: "State Name", data_type: "string", multi: false, placeholder: "Enter state name" },
      { key: "multiple_images", label: "Photos", data_type: "photo", multi: true, placeholder: "Upload photos" },
    ],
  },
  {
    layer_name: "POP",
    geometry_type: "Point",
    schema: [
      { key: "name", label: "POP Name", data_type: "string", multi: false, placeholder: "Enter POP name" },
      { key: "latitude", label: "Latitude", data_type: "float", multi: false, auto: true, placeholder: "Latitude (e.g., 22.5726)" },
      { key: "longitude", label: "Longitude", data_type: "float", multi: false, auto: true, placeholder: "Longitude (e.g., 88.3639)" },
      {
        key: "type", label: "Type", data_type: "string", multi: false, placeholder: "Select POP type", options: [
          { id: 1, value: "GP" }, { id: 2, value: "FPOI" }, { id: 3, value: "BHQ" }, { id: 4, value: "BLOCK ROUTER" },
        ]
      },
      { key: "block_name", label: "Block Name", data_type: "string", multi: false, placeholder: "Enter block name" },
      { key: "district_name", label: "District Name", data_type: "string", multi: false, placeholder: "Enter district name" },
      { key: "state_name", label: "State Name", data_type: "string", multi: false, placeholder: "Enter state name" },
      { key: "multiple_images", label: "Photos", data_type: "photo", multi: true, placeholder: "Upload POP photos" },
      {
        key: "status", label: "Status", data_type: "string", multi: false, placeholder: "Select status", options: [
          { id: 1, value: "ACTIVE" }, { id: 2, value: "NOT ACTIVE" },
        ]
      },
      { key: "earthing_status", label: "Earthing Status", data_type: "string", multi: false, placeholder: "Enter earthing status" },
      { key: "network_id", label: "Network Id", data_type: "string", multi: false, placeholder: "Enter network id" },
    ],
  },
  {
    layer_name: "OFC",
    geometry_type: "LineString",
    schema: [
      { key: "start_node", label: "Start Node", data_type: "string", multi: false, placeholder: "Enter start node" },
      { key: "end_node", label: "End Node", data_type: "string", multi: false, placeholder: "Enter end node" },
      { key: "length_mtr", label: "Length (mtr.)", data_type: "float", multi: false, placeholder: "Enter length in meters" },
      { key: "num_fibre", label: "Number of Fibres", data_type: "integer", multi: false, placeholder: "Enter fibre count" },
      {
        key: "type", label: "Type", data_type: "string", multi: false, placeholder: "Select OFC type", options: [
          { id: 1, value: "INCREMENTAL" }, { id: 2, value: "PROPOSED" }, { id: 3, value: "NOT FEASIBLE" },
        ]
      },
      { key: "block_name", label: "Block Name", data_type: "string", multi: false, placeholder: "Enter block name" },
      { key: "district_name", label: "District Name", data_type: "string", multi: false, placeholder: "Enter district name" },
      { key: "state_name", label: "State Name", data_type: "string", multi: false, placeholder: "Enter state name" },
    ],
  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function makeid() {
  return Math.random().toString(36).slice(2, 8);
}

function schemaToFields(schema) {
  return schema.map((f) => ({
    _id: makeid(),
    key: f.key ?? "",
    label: f.label ?? "",
    data_type: f.data_type ?? "string",
    placeholder: f.placeholder ?? "",
    multi: f.multi ?? false,
    auto: f.auto ?? false,
    auto_value: f.auto && f.default ? f.default : "",
    default: !f.auto && f.default ? f.default : "",
    options: f.options ?? [],
    _optInput: "",
    _expanded: false,
  }));
}

function emptyField() {
  return schemaToFields([{}])[0];
}

// ─── TypeBadge ────────────────────────────────────────────────────────────────

function TypeBadge({ dtype }) {
  const d = DTYPE_MAP[dtype];
  if (!d) return null;
  return (
    <Box component="span" sx={{
      fontSize: 10, px: 0.7, py: 0.15, borderRadius: "4px",
      fontFamily: "sans-serif", flexShrink: 0,
      background: d.bg, color: d.color, fontWeight: 500,
    }}>
      {d.badge}
    </Box>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

function Section({ title, badge, badgeColor = "optional", children, action }) {
  return (
    <Box sx={{ px: 1.5, py: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{
          fontSize: 10, fontWeight: 600, textTransform: "uppercase",
          letterSpacing: "0.06em", color: "text.secondary",
        }}>
          {title}
        </Typography>
        {badge && (
          <Typography variant="caption" sx={{
            fontSize: 10, px: 0.75, py: 0.1, borderRadius: "99px", fontWeight: 500,
            background: badgeColor === "required" ? "rgba(211,47,47,0.1)" : "rgba(46,125,50,0.1)",
            color: badgeColor === "required" ? "error.dark" : "success.dark",
          }}>
            {badge}
          </Typography>
        )}
        {action && <Box sx={{ ml: "auto" }}>{action}</Box>}
      </Box>
      {children}
    </Box>
  );
}

// ─── FieldRow ─────────────────────────────────────────────────────────────────

function FieldRow({ field, onChange, onDelete }) {
  const expanded = field._expanded;
  const update = (patch) => onChange({ ...field, ...patch });

  const addOption = () => {
    const val = field._optInput.trim();
    if (!val) return;
    const newId = field.options.length ? Math.max(...field.options.map((o) => o.id)) + 1 : 1;
    update({ options: [...field.options, { id: newId, value: val }], _optInput: "" });
  };

  const removeOption = (id) => update({ options: field.options.filter((o) => o.id !== id) });

  const supportsOptions = field.data_type === "string";
  const supportsAuto = ["float", "integer"].includes(field.data_type);
  const supportsMulti = field.data_type === "photo";

  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: "var(--border-radius-md)",
      mb: 0.75, overflow: "hidden",
      background: "var(--color-background-primary)",
    }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        px: 1, py: 0.6,
        background: expanded ? "action.hover" : "transparent",
        borderBottom: expanded ? "0.5px solid" : "none",
        borderColor: "divider",
      }}>
        <DragIndicatorIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0, cursor: "grab" }} />

        <TextField
          size="small" placeholder="field_key"
          value={field.key}
          onChange={(e) => update({ key: e.target.value.replace(/\s/g, "_").toLowerCase() })}
          InputProps={{ sx: { fontSize: 12, fontFamily: "var(--font-mono)", height: 26 } }}
          sx={{ flex: 1 }}
        />

        <Select
          size="small" value={field.data_type}
          onChange={(e) => update({ data_type: e.target.value, options: [], auto: false, auto_value: "" })}
          MenuProps={{ style: { zIndex: 2000 } }}
          sx={{ fontSize: 12, height: 26, minWidth: 95 }}
        >
          {DTYPES.map((d) => (
            <MenuItem key={d.value} value={d.value} sx={{ fontSize: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <TypeBadge dtype={d.value} />{d.label}
              </Box>
            </MenuItem>
          ))}
        </Select>

        {field.auto && <Chip label="auto" size="small" sx={{ fontSize: 10, height: 16, background: "#FAEEDA", color: "#633806", "& .MuiChip-label": { px: 0.75 } }} />}
        {field.options?.length > 0 && <Chip label={`${field.options.length} opts`} size="small" sx={{ fontSize: 10, height: 16, background: "#E6F1FB", color: "#185FA5", "& .MuiChip-label": { px: 0.75 } }} />}
        {field.multi && <Chip label="multi" size="small" sx={{ fontSize: 10, height: 16, background: "#EEEDFE", color: "#3C3489", "& .MuiChip-label": { px: 0.75 } }} />}

        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => update({ _expanded: !expanded })}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
        </IconButton>
        <Tooltip title="Remove field">
          <IconButton size="small" sx={{ p: 0.25 }} onClick={onDelete}>
            <DeleteOutlineIcon sx={{ fontSize: 15, color: "error.light" }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 1.25, py: 1, display: "flex", flexDirection: "column", gap: 1 }}>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>Label</Typography>
              <TextField fullWidth size="small" placeholder="Display label"
                value={field.label}
                onChange={(e) => update({ label: e.target.value })}
                InputProps={{ sx: { fontSize: 12, height: 26 } }} sx={{ mt: 0.4 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>Placeholder</Typography>
              <TextField fullWidth size="small" placeholder="Hint text"
                value={field.placeholder}
                onChange={(e) => update({ placeholder: e.target.value })}
                InputProps={{ sx: { fontSize: 12, height: 26 } }} sx={{ mt: 0.4 }}
              />
            </Box>
          </Box>

          {supportsMulti && (
            <FormControlLabel
              control={<Switch size="small" checked={field.multi} onChange={(e) => update({ multi: e.target.checked })} />}
              label={<Typography variant="caption" sx={{ fontSize: 12 }}>Allow multiple uploads</Typography>}
              sx={{ ml: 0 }}
            />
          )}

          {supportsAuto && (
            <Box>
              <FormControlLabel
                control={<Switch size="small" checked={field.auto} onChange={(e) => update({ auto: e.target.checked, auto_value: "" })} />}
                label={<Typography variant="caption" sx={{ fontSize: 12 }}>Auto value</Typography>}
                sx={{ ml: 0, mb: 0.25 }}
              />
              {field.auto && (
                <Select fullWidth size="small" displayEmpty
                  value={field.auto_value}
                  onChange={(e) => update({ auto_value: e.target.value })}
                  MenuProps={{ style: { zIndex: 2000 } }}
                  renderValue={(v) => v || <em style={{ color: "rgba(0,0,0,0.38)", fontSize: 12 }}>— select auto value —</em>}
                  sx={{ fontSize: 12, fontFamily: "var(--font-mono)" }}
                >
                  {AUTO_VALUES.map((a) => (
                    <MenuItem key={a.value} value={a.value} sx={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{a.label}</MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          )}

          {!field.auto && !supportsMulti && !supportsOptions && (
            <Box>
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>Default value</Typography>
              <TextField fullWidth size="small" placeholder="Optional default"
                value={field.default}
                onChange={(e) => update({ default: e.target.value })}
                InputProps={{ sx: { fontSize: 12, height: 26 } }} sx={{ mt: 0.4 }}
              />
            </Box>
          )}

          {supportsOptions && (
            <Box>
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>
                Options{field.options.length > 0 ? ` (${field.options.length})` : ""}
              </Typography>
              {field.options.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5, mb: 0.5 }}>
                  {field.options.map((opt) => (
                    <Chip key={opt.id} label={`${opt.id}. ${opt.value}`} size="small"
                      onDelete={() => removeOption(opt.id)}
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                <TextField size="small" placeholder="Add option…"
                  value={field._optInput}
                  onChange={(e) => update({ _optInput: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                  InputProps={{ sx: { fontSize: 12, height: 26 } }} sx={{ flex: 1 }}
                />
                <Button size="small" variant="outlined"
                  onClick={addOption} disabled={!field._optInput.trim()}
                  sx={{ minWidth: 0, px: 1, height: 26, fontSize: 11 }}
                >Add</Button>
              </Box>
              <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>
                Press Enter or click Add.
              </Typography>
            </Box>
          )}

        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LayerBuilderTool({ title, map, layers, onClose, onNewLayerCallback, project }) {
  const [layerName, setLayerName] = useState("");
  const [geomType, setGeomType] = useState("Point");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const logsEndRef = useRef(null);

  const pushLog = useCallback((type, message) =>
    setLogs((prev) => [...prev, { type, message }]), []);

  // ── auto-scroll log to bottom ─────────────────────────────────────────────
  // (logsEndRef is now a stable useRef — was previously a plain object recreated every render)
  // ── apply template ────────────────────────────────────────────────────────
  const applyTemplate = (tpl) => {
    setLayerName(tpl.layer_name);
    setGeomType(tpl.geometry_type);
    setFields(schemaToFields(tpl.schema));
    setActiveTemplate(tpl.layer_name);
    setLogs([]);
  };

  // ── field management ──────────────────────────────────────────────────────
  const addField = useCallback(() => setFields((p) => [...p, emptyField()]), []);
  const updateField = useCallback((idx, patch) => setFields((p) => p.map((f, i) => i === idx ? patch : f)), []);
  const deleteField = useCallback((idx) => setFields((p) => p.filter((_, i) => i !== idx)), []);

  // ── build payload ─────────────────────────────────────────────────────────
  const buildPayload = () => ({
    name: layerName,
    geometry_type: geomType.toUpperCase(),
    property_schema: fields.map((f) => {
      const out = {
        key: f.key,
        label: f.label || f.key,
        data_type: f.data_type,
        placeholder: f.placeholder || "",
        multi: f.multi,
      };
      if (f.auto && f.auto_value) { out.auto = true; out.default = f.auto_value; }
      if (!f.auto && f.default) out.default = f.default;
      if (f.options?.length) out.options = f.options;
      return out;
    }),
  });

  const canRun = !!layerName && fields.length > 0;

  const handleRun = async () => {
    if (!canRun) return;
    setLogs([]);
    setLoading(true);
    pushLog("info", `Creating layer "${layerName}"…`);

    try {
      const res = await fetch(`${LAYER_URL}/${project.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (res.ok) {
        const data = await res.json();
        pushLog("info", `Layer created successfully "${data.name}"…`);
        onNewLayerCallback(data);
        pushLog("success", `Layer "${data.name}" added successfully.`);
      }
    } catch (err) {
      pushLog("error", `Failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <ToolDialog title={title} open onClose={onClose} sx={{ width: 600 }}>

      <Box sx={{ maxHeight: "68vh", overflowY: "auto" }}>

        {/* ── Templates ── */}
        <Section title="Templates">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {TEMPLATES.map((tpl) => {
              const gs = GEOM_STYLE[tpl.geometry_type] ?? {};
              const isActive = activeTemplate === tpl.layer_name;
              return (
                <Tooltip key={tpl.layer_name} title={`${tpl.geometry_type} · ${tpl.schema.length} fields — click to apply`}>
                  <Box
                    onClick={() => applyTemplate(tpl)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.6,
                      px: 1, py: 0.4,
                      border: "0.5px solid",
                      borderColor: isActive ? "primary.main" : "divider",
                      borderRadius: "var(--border-radius-md)",
                      background: isActive ? "rgba(25,118,210,0.07)" : "action.hover",
                      cursor: "pointer", userSelect: "none",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "primary.light", background: "rgba(25,118,210,0.04)" },
                    }}
                  >
                    <Box sx={{
                      width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                      background: gs.bg, border: `1.5px solid ${gs.border}`,
                    }} />
                    <Typography variant="caption" sx={{
                      fontSize: 12, fontWeight: isActive ? 500 : 400,
                      color: isActive ? "primary.main" : "text.primary",
                    }}>
                      {tpl.layer_name}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>
                      {tpl.schema.length}f
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled", mt: 0.75, display: "block" }}>
            Click to prefill name, geometry, and all schema fields. You can edit after applying.
          </Typography>
        </Section>

        <Divider />

        {/* ── Layer identity ── */}
        <Section title="Layer" badge="Required" badgeColor="required">
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>Layer name</Typography>
              <TextField fullWidth size="small" placeholder="e.g. Landmarks"
                value={layerName}
                onChange={(e) => { setLayerName(e.target.value); setActiveTemplate(null); }}
                InputProps={{ sx: { fontSize: 12, height: 32 } }} sx={{ mt: 0.5 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>Geometry type</Typography>
              <Select fullWidth size="small"
                value={geomType}
                onChange={(e) => setGeomType(e.target.value)}
                MenuProps={{ style: { zIndex: 2000 } }}
                sx={{ mt: 0.5, fontSize: 12 }}
              >
                {GEOM_TYPES.map((g) => (
                  <MenuItem key={g} value={g} sx={{ fontSize: 12 }}>{g}</MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Section>

        <Divider />

        {/* ── Schema fields ── */}
        <Section
          title="Schema fields"
          badge="Required" badgeColor="required"
          action={
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {DTYPES.map((d) => (
                <Box key={d.value} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <TypeBadge dtype={d.value} />
                  <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>{d.label}</Typography>
                </Box>
              ))}
            </Box>
          }
        >
          {fields.length > 0 && (
            <Box sx={{ display: "flex", px: 1, mb: 0.5, gap: 0.5 }}>
              <Box sx={{ width: 18 }} />
              <Typography variant="caption" sx={{ flex: 1, fontSize: 10, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>Key</Typography>
              <Typography variant="caption" sx={{ width: 95, fontSize: 10, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>Type</Typography>
              <Box sx={{ width: 56 }} />
            </Box>
          )}

          {fields.map((f, idx) => (
            <FieldRow
              key={f._id}
              field={f}
              onChange={(patch) => updateField(idx, patch)}
              onDelete={() => deleteField(idx)}
            />
          ))}

          <Button fullWidth variant="outlined" size="small"
            startIcon={<AddIcon />} onClick={addField}
            sx={{ mt: 0.5, fontSize: 12, textTransform: "none", borderStyle: "dashed" }}
          >
            Add field
          </Button>
        </Section>

      </Box>

      <Divider />

      {/* ── Log ── */}
      {logs.length > 0 && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0 }}>
          <Box sx={{
            maxHeight: 100, overflowY: "auto",
            background: "action.hover",
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 1, p: 0.75,
            display: "flex", flexDirection: "column", gap: 0.25,
          }}>
            {logs.map((log, i) => (
              <Box key={i} sx={{ display: "flex", gap: 0.6 }}>
                <Typography variant="caption" sx={{
                  fontSize: 11, flexShrink: 0,
                  color: log.type === "error" ? "error.main" : log.type === "success" ? "success.main" : "text.disabled",
                }}>
                  {log.type === "error" ? "✕" : log.type === "success" ? "✓" : "›"}
                </Typography>
                <Typography variant="caption" sx={{
                  fontSize: 11, fontFamily: "var(--font-mono)", lineHeight: 1.5,
                  color: log.type === "error" ? "error.main" : log.type === "success" ? "success.main" : "text.secondary",
                }}>
                  {log.message}
                </Typography>
              </Box>
            ))}
            <div ref={(el) => { logsEndRef.current = el; }} />
          </Box>
        </Box>
      )}

      {/* ── Run ── */}
      <Box sx={{ px: 1.5, py: 1.25 }}>
        {!canRun && (
          <Typography variant="caption" color="text.disabled" display="block" mb={0.75}>
            Enter a layer name and add at least one field, or pick a template above
          </Typography>
        )}
        <Button fullWidth variant="contained" size="small"
          disabled={!canRun || loading} onClick={handleRun}
          startIcon={loading ? null : <PlayArrowIcon fontSize="small" />}
        >
          {loading ? "Creating…" : "Create Layer"}
        </Button>
      </Box>

    </ToolDialog>
  );
}